import React, { useState, useEffect } from 'react';

const SCHEDULES = {
  day: {
    SOS: { start: '08:15' },
    B1: { end: '10:00', start: '10:20' },
    Lunch: { end: '12:43', start: '13:30' },
    B2: { end: '16:00', start: '16:20' },
    EOS: { end: '18:25' },
  },
  night: {
    SOS: { start: '19:15' },
    B1: { end: '21:00', start: '21:20' },
    Lunch: { end: '23:43', start: '00:30' },
    B2: { end: '03:00', start: '03:20' },
    EOS: { end: '05:25' },
  },
};

const BRT_MINUTES = 525;

// BRT minutes per clock hour (mirrors fclm.js day/night schedule), used to value
// lost minutes at that hour's actual run rate (cartons that hour ÷ BRT that hour).
const BRT_BY_HOUR = {
  day:   { '08': 45, '09': 60, '10': 45, '11': 60, '12': 40, '13': 30, '14': 60, '15': 60, '16': 40, '17': 60, '18': 25 },
  night: { '19': 45, '20': 60, '21': 45, '22': 60, '23': 40, '00': 30, '01': 60, '02': 60, '03': 40, '04': 60, '05': 25 },
};

// Per-site Fast Start schedules (same rules as OLM1's above): SOS = shift start
// +15; break end = break start, restart = break end +5; lunch end = lunch start
// -2, restart = lunch end +15; EOS = shift end -5. Breaks/BRT match the
// backend SITE_SHIFTS in fclm.js. Sites not listed use the OLM1 tables.
const SBD3_DAY = {
  SOS: { start: '07:15' },
  B1: { end: '09:00', start: '09:20' },
  Lunch: { end: '11:28', start: '12:15' },
  B2: { end: '15:00', start: '15:20' },
  EOS: { end: '17:25' },
};
const SITE_SCHEDULES = {
  SBD3: {
    day: SBD3_DAY,
    night: {
      SOS: { start: '18:45' },
      B1: { end: '20:00', start: '20:20' },
      Lunch: { end: '22:58', start: '23:45' },
      B2: { end: '02:00', start: '02:20' },
      EOS: { end: '04:55' },
    },
  },
  SCK8: {
    day: SBD3_DAY,
    night: {
      SOS: { start: '18:15' },
      B1: { end: '20:00', start: '20:20' },
      Lunch: { end: '22:28', start: '23:15' },
      B2: { end: '02:00', start: '02:20' },
      EOS: { end: '04:25' },
    },
  },
};
const SBD3_DAY_BRT = { '07': 45, '08': 60, '09': 45, '10': 60, '11': 25, '12': 45, '13': 60, '14': 60, '15': 40, '16': 60, '17': 25 };
const SITE_BRT_BY_HOUR = {
  SBD3: {
    day: SBD3_DAY_BRT,
    night: { '18': 15, '19': 60, '20': 45, '21': 60, '22': 60, '23': 10, '00': 60, '01': 60, '02': 40, '03': 60, '04': 55 },
  },
  SCK8: {
    day: SBD3_DAY_BRT,
    night: { '18': 45, '19': 60, '20': 45, '21': 60, '22': 25, '23': 45, '00': 60, '01': 60, '02': 40, '03': 60, '04': 25 },
  },
};
// AROS is OLM1-only; these sites show PID columns only.
const NO_AROS_SITES = ['SBD3', 'SCK8'];

function toMinutes(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

function formatTime(h, m) {
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default function FastStart({ api, site, date, shiftType }) {
  const [minuteData, setMinuteData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date || !shiftType) return;
    setLoading(true);
    fetch(`${api}/api/fclm/cache?site=${site}&date=MP_HOURLY_${date}&shift_type=${shiftType}`)
      .then(r => r.json())
      .then(data => {
        // We need per-minute data - check if we have it or need to fetch from MP_MINUTE
        setMinuteData(data);
        setLoading(false);
      })
      .catch(() => { setMinuteData(null); setLoading(false); });
  }, [api, site, date, shiftType]);

  // For now, show placeholder - minute data needs the CSV endpoint
  // We'll use a dedicated minute-level cache
  const [minuteRaw, setMinuteRaw] = useState(null);
  useEffect(() => {
    if (!date || !shiftType) return;
    fetch(`${api}/api/fclm/cache?site=${site}&date=MP_MINUTES_${date}&shift_type=${shiftType}`)
      .then(r => r.json())
      .then(data => { if (data?.minutes) setMinuteRaw(data); })
      .catch(() => {});
  }, [api, site, date, shiftType]);

  const siteSchedules = SITE_SCHEDULES[site] || SCHEDULES;
  const schedule = siteSchedules[shiftType] || siteSchedules.day;
  const brtByHour = SITE_BRT_BY_HOUR[site] || BRT_BY_HOUR;
  const hideAros = NO_AROS_SITES.includes(site);

  // Analyze minute data to find actual start/end times
  const analyze = (path) => {
    if (!minuteRaw?.minutes) return {};
    const minutes = minuteRaw.minutes;
    const key = path === 'PID' ? 'pid' : 'aros';

    // Sort all minutes chronologically, handling night shift midnight crossing
    const sortedMinutes = Object.entries(minutes).sort((a, b) => {
      let ma = toMinutes(a[0]), mb = toMinutes(b[0]);
      if (shiftType === 'night') { if (ma < 12 * 60) ma += 24 * 60; if (mb < 12 * 60) mb += 24 * 60; }
      return ma - mb;
    });

    const adjustMin = (m) => (shiftType === 'night' && m < 12 * 60) ? m + 24 * 60 : m;

    // Find first minute with data >= expectedStart
    const findFirstAfter = (expectedStart) => {
      const expMin = adjustMin(toMinutes(expectedStart));
      for (const [time, vals] of sortedMinutes) {
        const min = adjustMin(toMinutes(time));
        if (min >= expMin && (vals[key] || 0) > 0) return time;
      }
      return null;
    };

    // Find last minute with data <= expectedEnd
    const findLastBefore = (expectedEnd) => {
      const expMin = adjustMin(toMinutes(expectedEnd));
      let last = null;
      for (const [time, vals] of sortedMinutes) {
        const min = adjustMin(toMinutes(time));
        if (min <= expMin && (vals[key] || 0) > 0) last = time;
      }
      return last;
    };

    // Find first minute with data in a window (after gap)
    const findFirstInWindow = (windowStart, windowEnd) => {
      const startMin = adjustMin(toMinutes(windowStart));
      const endMin = adjustMin(toMinutes(windowEnd));
      for (const [time, vals] of sortedMinutes) {
        const min = adjustMin(toMinutes(time));
        if (min >= startMin && min <= endMin && (vals[key] || 0) > 0) return time;
      }
      return null;
    };

    // Find last minute with data in a window (before gap)
    const findLastInWindow = (windowStart, windowEnd) => {
      const startMin = adjustMin(toMinutes(windowStart));
      const endMin = adjustMin(toMinutes(windowEnd));
      let last = null;
      for (const [time, vals] of sortedMinutes) {
        const min = adjustMin(toMinutes(time));
        if (min >= startMin && min <= endMin && (vals[key] || 0) > 0) last = time;
      }
      return last;
    };

    return {
      SOS: { actualStart: findFirstAfter(schedule.SOS.start) },
      B1: { 
        actualEnd: findLastInWindow(schedule.SOS.start, schedule.B1.end),
        actualStart: findFirstInWindow(schedule.B1.start, schedule.Lunch?.end || '23:59')
      },
      Lunch: {
        actualEnd: findLastInWindow(schedule.B1.start, schedule.Lunch.end),
        actualStart: findFirstInWindow(schedule.Lunch.start, schedule.B2?.end || '23:59')
      },
      B2: {
        actualEnd: findLastInWindow(schedule.Lunch.start, schedule.B2.end),
        actualStart: findFirstInWindow(schedule.B2.start, schedule.EOS?.end || '23:59')
      },
      EOS: { actualEnd: findLastBefore(schedule.EOS.end) },
    };
  };

  const calcDelay = (expected, actual) => {
    if (!actual) return null;
    let expMin = toMinutes(expected);
    let actMin = toMinutes(actual);
    if (shiftType === 'night') {
      if (expMin < 12 * 60) expMin += 24 * 60;
      if (actMin < 12 * 60) actMin += 24 * 60;
    }
    return actMin - expMin;
  };

  // Current Pacific time for future-quarter detection
  const nowPac = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const nowMin = nowPac.getHours() * 60 + nowPac.getMinutes();
  const adjustNow = shiftType === 'night' && nowMin < 12 * 60 ? nowMin + 24 * 60 : nowMin;
  const isToday = date === `${nowPac.getFullYear()}-${String(nowPac.getMonth()+1).padStart(2,'0')}-${String(nowPac.getDate()).padStart(2,'0')}`;

  // Check if a time point is in the future (only matters for today)
  const isFuture = (timeStr) => {
    if (!isToday) return false;
    let min = toMinutes(timeStr);
    if (shiftType === 'night' && min < 12 * 60) min += 24 * 60;
    return min > adjustNow;
  };

  // Check if a path is active in a quarter (>100 cartons in first hour of quarter)

  // Check if PID/AROS are active in a quarter (>100 cartons in first hour of quarter)
  const isPathActiveInQuarter = (path, quarterStart) => {
    if (!minuteRaw?.minutes) return false;
    const key = path === 'PID' ? 'pid' : 'aros';
    const startMin = shiftType === 'night' && toMinutes(quarterStart) < 12*60 ? toMinutes(quarterStart) + 24*60 : toMinutes(quarterStart);
    const endMin = startMin + 60;
    let total = 0;
    for (const [time, vals] of Object.entries(minuteRaw.minutes)) {
      let min = toMinutes(time);
      if (shiftType === 'night' && min < 12*60) min += 24*60;
      if (min >= startMin && min < endMin) total += vals[key] || 0;
    }
    return total >= 100;
  };

  // Check if path has ANY activity in the full shift
  const isPathActiveAnywhere = (path) => {
    if (!minuteRaw?.minutes) return false;
    const key = path === 'PID' ? 'pid' : 'aros';
    let total = 0;
    for (const vals of Object.values(minuteRaw.minutes)) total += vals[key] || 0;
    return total >= 200;
  };

  const pidAnalysis = analyze('PID');
  const arosAnalysis = analyze('AROS');

  const pidActive = isPathActiveAnywhere('PID');
  const arosActive = !hideAros && isPathActiveAnywhere('AROS');

  // Actual cartons a path produced in a given clock hour (HH), from per-minute data
  const hourCartons = (hh, pathKey) => {
    let sum = 0;
    for (const [t, v] of Object.entries(minuteRaw?.minutes || {})) {
      if (String(t).slice(0, 2) === hh) sum += v[pathKey] || 0;
    }
    return sum;
  };

  // Per-row minutes lost + cartons lost — single source of truth for row cells and TOTAL.
  // Cartons lost values each path's lost minutes at that hour's actual rate:
  //   (cartons the path ran that hour ÷ BRT minutes that hour) × minutes lost, summed over paths.
  const rowLost = (label, pidResult, arosResult, schedItem) => {
    const eventTime = schedItem.start || schedItem.end;
    if (isToday && isFuture(eventTime)) return { pid: 0, aros: 0, cartons: 0, future: true };
    const pidStart = pidResult?.actualStart, pidEnd = pidResult?.actualEnd;
    const arosStart = arosResult?.actualStart, arosEnd = arosResult?.actualEnd;
    let pidMinLost = 0, arosMinLost = 0;
    if (label === 'SOS') {
      pidMinLost = pidActive && isPathActiveInQuarter('PID', schedItem.start) ? Math.max(calcDelay(schedItem.start, pidStart) || 0, 0) : 0;
      arosMinLost = arosActive && isPathActiveInQuarter('AROS', schedItem.start) ? Math.max(calcDelay(schedItem.start, arosStart) || 0, 0) : 0;
    } else if (label === 'EOS') {
      pidMinLost = pidActive ? Math.max(calcDelay(pidEnd, schedItem.end) || 0, 0) : 0;
      arosMinLost = arosActive ? Math.max(calcDelay(arosEnd, schedItem.end) || 0, 0) : 0;
    } else {
      const pidEarlyOff = pidActive && pidEnd ? Math.max(calcDelay(pidEnd, schedItem.end) || 0, 0) : 0;
      const pidLateStart = pidActive && pidStart ? Math.max(calcDelay(schedItem.start, pidStart) || 0, 0) : 0;
      pidMinLost = pidEarlyOff + pidLateStart;
      const arosEarlyOff = arosActive && arosEnd ? Math.max(calcDelay(arosEnd, schedItem.end) || 0, 0) : 0;
      const arosLateStart = arosActive && arosStart ? Math.max(calcDelay(schedItem.start, arosStart) || 0, 0) : 0;
      arosMinLost = arosEarlyOff + arosLateStart;
    }
    // Rate hour: SOS uses its start, EOS its end, breaks use the restart time
    const anchor = label === 'EOS' ? schedItem.end : schedItem.start;
    const hh = String(anchor || '').slice(0, 2);
    const hourBRT = (brtByHour[shiftType] || {})[hh] || 60;
    const cartons = Math.round((hourCartons(hh, 'pid') / hourBRT) * pidMinLost + (hourCartons(hh, 'aros') / hourBRT) * arosMinLost);
    return { pid: pidMinLost, aros: arosMinLost, cartons };
  };

  const cellStyle = { padding: '3px 6px', textAlign: 'center', fontSize: '0.7rem', border: '1px solid #ccc' };
  const headerStyle = { ...cellStyle, fontWeight: 700, background: '#232f3e', color: '#fff' };
  const labelStyle = { ...cellStyle, textAlign: 'left', fontWeight: 600 };

  const renderRow = (label, pidResult, arosResult, schedItem) => {
    // Check if this event is in the future
    const eventTime = schedItem.start || schedItem.end;
    if (isToday && isFuture(eventTime)) {
      return (
        <tr key={label}>
          <td style={labelStyle}>{label}</td>
          <td colSpan={hideAros ? 4 : 6} style={{ ...cellStyle, color: '#999', fontStyle: 'italic' }}>—</td>
        </tr>
      );
    }

    const pidStart = pidResult?.actualStart;
    const pidEnd = pidResult?.actualEnd;
    const arosStart = arosResult?.actualStart;
    const arosEnd = arosResult?.actualEnd;

    const { pid: pidMinLost, aros: arosMinLost, cartons: cartonsLost } = rowLost(label, pidResult, arosResult, schedItem);
    const totalMin = pidMinLost + arosMinLost;

    return (
      <tr key={label}>
        <td style={labelStyle}>{label}</td>
        <td style={cellStyle}>{pidActive ? (pidEnd || '—') : '—'}</td>
        <td style={cellStyle}>{pidActive ? (pidStart || '—') : '—'}</td>
        {!hideAros && <td style={cellStyle}>{arosActive ? (arosEnd || '—') : '—'}</td>}
        {!hideAros && <td style={cellStyle}>{arosActive ? (arosStart || '—') : '—'}</td>}
        <td style={{ ...cellStyle, fontWeight: 600, color: totalMin > 0 ? '#ef5350' : '#4caf50' }}>{totalMin || '—'}</td>
        <td style={{ ...cellStyle, fontWeight: 600, color: cartonsLost > 0 ? '#ef5350' : '#4caf50' }}>{cartonsLost || '—'}</td>
      </tr>
    );
  };

  if (!minuteRaw?.minutes) {
    return <div style={{ fontSize: '0.75rem', color: '#999', padding: 8 }}>Fast Start analysis requires per-minute MonitorPortal data.</div>;
  }

  // TOTAL = sum of the exact per-row losses shown in the table (single source of truth)
  const ROW_DEFS = [
    ['SOS', pidAnalysis.SOS, arosAnalysis.SOS, schedule.SOS],
    ['B1', pidAnalysis.B1, arosAnalysis.B1, schedule.B1],
    ['Lunch', pidAnalysis.Lunch, arosAnalysis.Lunch, schedule.Lunch],
    ['B2', pidAnalysis.B2, arosAnalysis.B2, schedule.B2],
    ['EOS', pidAnalysis.EOS, arosAnalysis.EOS, schedule.EOS],
  ];
  const getMinutesLostSum = (which) => ROW_DEFS.reduce((s, [l, p, a, sc]) => s + rowLost(l, p, a, sc)[which], 0);
  const pidTotalLost = getMinutesLostSum('pid');
  const arosTotalLost = getMinutesLostSum('aros');
  const totalCartonsLost = getMinutesLostSum('cartons');

  return (
    <div style={{ marginTop: 16 }}>
      <h4 style={{ fontSize: '0.85rem', margin: '0 0 8px' }}>⚡ Fast Start Analysis</h4>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.7rem' }}>
        <thead>
          <tr>
            <th style={headerStyle}></th>
            <th colSpan={2} style={headerStyle}>PID</th>
            {!hideAros && <th colSpan={2} style={headerStyle}>AROS</th>}
            <th style={headerStyle}>Total Min</th>
            <th style={headerStyle}>Cartons Lost</th>
          </tr>
          <tr>
            <th style={headerStyle}></th>
            <th style={headerStyle}>End</th>
            <th style={headerStyle}>Start</th>
            {!hideAros && <th style={headerStyle}>End</th>}
            {!hideAros && <th style={headerStyle}>Start</th>}
            <th style={headerStyle}>Lost</th>
            <th style={headerStyle}>Lost</th>
          </tr>
        </thead>
        <tbody>
          {renderRow('SOS', pidAnalysis.SOS, arosAnalysis.SOS, schedule.SOS)}
          {renderRow('B1', pidAnalysis.B1, arosAnalysis.B1, schedule.B1)}
          {renderRow('Lunch', pidAnalysis.Lunch, arosAnalysis.Lunch, schedule.Lunch)}
          {renderRow('B2', pidAnalysis.B2, arosAnalysis.B2, schedule.B2)}
          {renderRow('EOS', pidAnalysis.EOS, arosAnalysis.EOS, schedule.EOS)}
          <tr style={{ borderTop: '2px solid #000' }}>
            <td style={{ ...labelStyle, fontWeight: 700 }}>TOTAL</td>
            <td colSpan={hideAros ? 2 : 4} style={cellStyle}></td>
            <td style={{ ...cellStyle, fontWeight: 700, color: '#ef5350' }}>{pidTotalLost + arosTotalLost}</td>
            <td style={{ ...cellStyle, fontWeight: 700, color: '#ef5350' }}>{totalCartonsLost}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
